import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiZap,
  FiEye,
  FiRefreshCw,
  FiAlertTriangle,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';

import { useWorkflows } from '../../../hooks/useWorkflows';
import MacSubscribePrompt from '../../amps/MacSubscribePrompt';
import WorkflowDetailModal from './WorkflowDetailModal';
import type {
  Workflow,
  PublicWorkflow
} from '../../../services/workflowService';

const STATUS_COLORS: Record<string, string> = {
  running: '#10b981',
  draft: '#f59e0b',
  paused: '#3b82f6',
  stopped: '#6b7280'
};
const statusColor = (s: string) => STATUS_COLORS[s] ?? '#6b7280';

type Tab = 'mine' | 'global';

const TABS: { key: Tab; label: string }[] = [
  { key: 'mine', label: 'My Workflows' },
  { key: 'global', label: 'Global Workflows' }
];

const WorkflowsWidget: React.FC = () => {
  const [tab, setTab] = useState<Tab>('mine');
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    null
  );
  const [isSelectedMine, setIsSelectedMine] = useState(true);

  const {
    workflows,
    meta,
    isLoading,
    error,
    fetchWorkflows,
    publicWorkflows,
    publicMeta,
    isLoadingPublic,
    publicError,
    fetchPublicWorkflows,
    notSubscribed,
    isCloning,
    cloneSuccess,
    cloneError,
    cloneWorkflow,
    isViewingInMac,
    viewInMacError,
    viewInMac,
    goToPage,
    goToPublicPage
  } = useWorkflows({ perPage: 5 });

  const isMine = tab === 'mine';
  const items = isMine ? workflows : publicWorkflows;
  const loading = isMine ? isLoading : isLoadingPublic;
  const err = isMine ? error : publicError;
  const retry = isMine ? fetchWorkflows : fetchPublicWorkflows;
  const page = isMine
    ? (meta?.current_page ?? 1)
    : (publicMeta?.current_page ?? 1);
  const totalPages = isMine
    ? (meta?.total_pages ?? 1)
    : (publicMeta?.total_pages ?? 1);
  const onPage = isMine ? goToPage : goToPublicPage;

  const openMineModal = (wf: Workflow) => {
    setSelectedWorkflow(wf);
    setIsSelectedMine(true);
  };

  const openGlobalModal = (wf: PublicWorkflow) => {
    setSelectedWorkflow(wf as unknown as Workflow);
    setIsSelectedMine(false);
  };

  const actionSlot = selectedWorkflow ? (
    isSelectedMine ? (
      <motion.button
        onClick={() => viewInMac(selectedWorkflow.key)}
        disabled={isViewingInMac === selectedWorkflow.key}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className={`mws-btn ${viewInMacError === selectedWorkflow.key ? 'mws-btn--view-error' : 'mws-btn--view'}`}
      >
        <FiZap size={11} />
        {isViewingInMac === selectedWorkflow.key
          ? 'Opening…'
          : viewInMacError === selectedWorkflow.key
            ? 'Failed'
            : 'View in MAC'}
      </motion.button>
    ) : (
      <motion.button
        onClick={() => cloneWorkflow(selectedWorkflow.key)}
        disabled={
          isCloning === selectedWorkflow.key ||
          cloneSuccess === selectedWorkflow.key
        }
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className={`mws-btn ${
          cloneSuccess === selectedWorkflow.key
            ? 'mws-btn--clone-success'
            : cloneError === selectedWorkflow.key
              ? 'mws-btn--clone-error'
              : 'mws-btn--clone'
        }`}
      >
        <FiZap size={11} />
        {isCloning === selectedWorkflow.key
          ? 'Cloning…'
          : cloneSuccess === selectedWorkflow.key
            ? 'Cloned!'
            : cloneError === selectedWorkflow.key
              ? 'Failed'
              : 'Clone'}
      </motion.button>
    )
  ) : null;

  return (
    <>
      <div className='dw-card' style={{ padding: '12px 14px' }}>
        <div className='dw-card__label' style={{ marginBottom: 8 }}>
          Workflows
        </div>

        <div className='mws-tabs'>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`mws-tab${tab === key ? ' mws-tab--active' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        {isMine && notSubscribed ? (
          <MacSubscribePrompt />
        ) : loading ? (
          <div className='mws-skeletons'>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className='dw-skel mws-skel-row' />
            ))}
          </div>
        ) : err ? (
          <div className='mws-error'>
            <span className='mws-error__msg'>
              <FiAlertTriangle size={12} /> {err}
            </span>
            <button onClick={retry} className='mws-error__retry'>
              <FiRefreshCw size={10} /> Retry
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className='mws-empty'>No workflows found</div>
        ) : (
          <div className='mws-list'>
            {isMine
              ? (items as Workflow[]).map(wf => {
                  const thisViewErr = viewInMacError === wf.key;
                  const triggerName =
                    wf.workflow_trigger?.filter_condition?.trigger_category
                      ?.name;

                  return (
                    <div key={wf.id} className='mws-item'>
                      <span
                        className='mws-item__dot'
                        style={{ background: statusColor(wf.status) }}
                      />
                      <div className='mws-item__info'>
                        <div className='mws-item__info-name'>{wf.name}</div>
                        <div className='mws-item__info-sub'>
                          {wf.touchpoints.length} touchpoints
                          {triggerName && ` · ${triggerName}`}
                        </div>
                      </div>
                      <motion.button
                        onClick={() => openMineModal(wf)}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className={`mws-btn ${thisViewErr ? 'mws-btn--view-error' : 'mws-btn--view'}`}
                      >
                        <FiEye size={9} />
                        {thisViewErr ? 'Error' : 'View'}
                      </motion.button>
                    </div>
                  );
                })
              : (items as PublicWorkflow[]).map(wf => {
                  const thisCloning = isCloning === wf.key;
                  const thisSuccess = cloneSuccess === wf.key;
                  const thisError = cloneError === wf.key;
                  const triggerName =
                    wf.workflow_trigger?.filter_condition?.trigger_category
                      ?.name;
                  const cloneClass = `mws-btn ${thisSuccess ? 'mws-btn--clone-success' : thisError ? 'mws-btn--clone-error' : 'mws-btn--clone'}`;

                  return (
                    <div key={wf.id} className='mws-item'>
                      <span
                        className='mws-item__dot'
                        style={{ background: statusColor(wf.status) }}
                      />
                      <div className='mws-item__info'>
                        <div className='mws-item__info-name'>{wf.name}</div>
                        <div className='mws-item__info-sub'>
                          {wf.touchpoints.length} touchpoints
                          {triggerName && ` · ${triggerName}`}
                        </div>
                      </div>
                      <motion.button
                        onClick={() => openGlobalModal(wf)}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className='mws-btn mws-btn--view'
                      >
                        <FiEye size={9} />
                        View
                      </motion.button>
                      <motion.button
                        onClick={() => cloneWorkflow(wf.key)}
                        disabled={thisCloning || thisSuccess}
                        whileHover={thisCloning ? {} : { scale: 1.04 }}
                        whileTap={thisCloning ? {} : { scale: 0.96 }}
                        className={cloneClass}
                      >
                        <FiZap size={9} />
                        {thisCloning
                          ? 'Cloning…'
                          : thisSuccess
                            ? 'Cloned!'
                            : thisError
                              ? 'Failed'
                              : 'Clone'}
                      </motion.button>
                    </div>
                  );
                })}
          </div>
        )}

        {!loading && !err && totalPages > 1 && (
          <div className='mws-pagination'>
            <button
              className='mws-pagination__btn'
              onClick={() => onPage(page - 1)}
              disabled={page === 1}
            >
              <FiChevronLeft size={12} />
            </button>
            <span className='mws-pagination__count'>
              {page} / {totalPages}
            </span>
            <button
              className='mws-pagination__btn'
              onClick={() => onPage(page + 1)}
              disabled={page === totalPages}
            >
              <FiChevronRight size={12} />
            </button>
          </div>
        )}
      </div>

      <WorkflowDetailModal
        workflow={selectedWorkflow}
        isMine={isSelectedMine}
        onClose={() => setSelectedWorkflow(null)}
        actionSlot={actionSlot}
      />
    </>
  );
};

export default WorkflowsWidget;
